/**
 * /book — public deep-link page for Cal.com bookings.
 *
 * Use this URL in social-media bios, email signatures, ads, and printed
 * materials. It accepts an optional ?type=intro|demo query string so the
 * same root URL can be customized:
 *
 *   couplingwnc.com/book          → 30-min product demo (default)
 *   couplingwnc.com/book?type=intro  → 15-min intro chat
 *
 * Renders the Cal.com embed inline (no modal) for a focused, distraction-free
 * booking experience that works well on every device.
 */

import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Footer, GradientHero, SEO, breadcrumbSchema } from '@coupling/ui';
import Cal, { getCalApi } from '@calcom/embed-react';
import Header from '../components/Header';
import {
  CAL_BRAND_COLOR,
  CAL_EVENT_LABELS,
  CalEventType,
  calLink,
} from '../config/calcom';
import { seoConfig } from '../config/seo';

const isCalEventType = (v: string | null): v is CalEventType =>
  v === 'intro' || v === 'demo';

export const Book: React.FC = () => {
  const [params] = useSearchParams();
  const typeParam = params.get('type');
  const eventType: CalEventType = isCalEventType(typeParam) ? typeParam : 'demo';
  const labels = CAL_EVENT_LABELS[eventType];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cal = await getCalApi({ namespace: `book-page-${eventType}` });
        if (cancelled) return;
        cal('ui', {
          theme: 'light',
          cssVarsPerTheme: {
            light: { 'cal-brand': CAL_BRAND_COLOR },
            dark: { 'cal-brand': CAL_BRAND_COLOR },
          },
          hideEventTypeDetails: false,
          layout: 'month_view',
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Cal.com embed init failed on /book:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventType]);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${labels.seoTitle} - ${seoConfig.siteName}`}
        description={labels.seoDescription}
        canonical={`${seoConfig.siteUrl}/book`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/book`}
        jsonLd={breadcrumbSchema([
          { name: 'Home', url: seoConfig.siteUrl },
          { name: 'Book a call', url: `${seoConfig.siteUrl}/book` },
        ])}
      />
      <Header />

      <GradientHero
        title={labels.long}
        description={labels.description}
      />

      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl overflow-hidden border border-neutral-200 bg-white shadow-sm">
            <Cal
              key={eventType}
              namespace={`book-page-${eventType}`}
              calLink={calLink(eventType)}
              style={{ width: '100%', height: '100%', minHeight: '720px' }}
              config={{ layout: 'month_view' }}
            />
          </div>

          <p className="text-center text-sm text-neutral-500 mt-6">
            Need something different?{' '}
            <a
              href={eventType === 'intro' ? '/book' : '/book?type=intro'}
              className="text-brand-primary hover:underline font-medium"
            >
              {eventType === 'intro' ? 'Switch to a 30-min demo' : 'Switch to a 15-min intro chat'}
            </a>
            {' · '}
            <a href="/contact" className="text-brand-primary hover:underline font-medium">
              Send a message instead
            </a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Book;
